import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import tw from "twin.macro";
import client from "sanityClient";
import { useLanguage } from "../../helpers/languageSelector";

const BANNER_HEIGHT_VAR = "--info-banner-height";
const SCROLL_PX_PER_SEC = 40;

const marquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const Banner = styled.div`
  ${tw`top-0 z-40 w-full overflow-hidden bg-primary-500 text-gray-100`}
  position: sticky;
`;

const Viewport = styled.div`
  ${tw`relative w-full`}
`;

const MeasureRow = styled.div`
  ${tw`absolute pointer-events-none py-2 text-sm font-semibold tracking-wide`}
  left: 0;
  visibility: hidden;
  white-space: nowrap;
`;

const StaticRow = styled.div`
  ${tw`flex justify-center items-center py-2 px-4 text-sm font-semibold tracking-wide`}
  white-space: nowrap;
`;

const Track = styled.div`
  ${tw`flex items-center py-2 text-sm font-semibold tracking-wide`}
  width: max-content;
  white-space: nowrap;
  animation: ${marquee} ${({ $duration }) => $duration}s linear infinite;

  &:hover,
  &:focus-within {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Copy = styled.div`
  ${tw`flex items-center flex-shrink-0`}
`;

const Item = tw.span`px-4`;
const Separator = tw.span`text-primary-200 select-none`;

const renderItems = (items, { hidden } = {}) =>
  items.map((item, index) => (
    <React.Fragment key={`${hidden ? "h" : "v"}-${index}`}>
      {index > 0 && <Separator aria-hidden="true">•</Separator>}
      <Item>{item.text}</Item>
    </React.Fragment>
  ));

const InfoBanner = () => {
  const { currentLanguage } = useLanguage();
  const [items, setItems] = useState([]);
  const bannerRef = useRef(null);
  const viewportRef = useRef(null);
  const measureRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [duration, setDuration] = useState(20);

  useEffect(() => {
    let cancelled = false;

    client
      .fetch(
        `*[_type == "infoBanner" && language == $language][0]{
          active,
          items[]{ text, active }
        }`,
        { language: currentLanguage }
      )
      .then((data) => {
        if (cancelled) return;
        if (!data?.active) {
          setItems([]);
          return;
        }
        const activeItems = (data.items || []).filter(
          (item) => item?.active && typeof item.text === "string" && item.text.trim()
        );
        setItems(activeItems);
      })
      .catch((error) => {
        console.error("Error fetching info banner:", error);
        if (!cancelled) setItems([]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  useEffect(() => {
    const root = document.documentElement;
    if (!items.length) {
      root.style.setProperty(BANNER_HEIGHT_VAR, "0px");
      return undefined;
    }

    const setHeight = () => {
      const height = bannerRef.current?.offsetHeight || 0;
      root.style.setProperty(BANNER_HEIGHT_VAR, `${height}px`);
    };

    setHeight();
    window.addEventListener("resize", setHeight);
    return () => {
      window.removeEventListener("resize", setHeight);
      root.style.setProperty(BANNER_HEIGHT_VAR, "0px");
    };
  }, [items, shouldScroll]);

  useEffect(() => {
    if (!items.length) {
      setShouldScroll(false);
      return undefined;
    }

    const measure = () => {
      const viewport = viewportRef.current;
      const measureRow = measureRef.current;
      if (!viewport || !measureRow) return;
      const overflow = measureRow.scrollWidth > viewport.clientWidth + 1;
      setShouldScroll(overflow);
      setDuration(Math.max(12, measureRow.scrollWidth / SCROLL_PX_PER_SEC));
    };

    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer && viewportRef.current) observer.observe(viewportRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [items]);

  if (!items.length) return null;

  return (
    <Banner ref={bannerRef} role="region" aria-label="Site announcements">
      <Viewport ref={viewportRef}>
        <MeasureRow ref={measureRef} aria-hidden="true">
          {renderItems(items, { hidden: true })}
        </MeasureRow>
        {shouldScroll ? (
          <Track $duration={duration}>
            <Copy>
              {renderItems(items)}
              <Separator aria-hidden="true">•</Separator>
            </Copy>
            <Copy aria-hidden="true">
              {renderItems(items, { hidden: true })}
              <Separator>•</Separator>
            </Copy>
          </Track>
        ) : (
          <StaticRow>{renderItems(items)}</StaticRow>
        )}
      </Viewport>
    </Banner>
  );
};

export default InfoBanner;
